import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { Helmet } from 'react-helmet-async'
import { useLocation, useHistory } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { get } from 'utils/lodash'
import { getPageTitle } from 'utils/seo'
import { useAuthValue } from 'auth/AuthContext'
import { getUserInfo } from 'middleware/actions/login'
import { setSelectedLoginDetails } from 'slices/login/LoginPersistSlice'
import { isSingleAccount, getFirstAccountDetails } from 'data/select-account/selectAccount'

import routes, { isPublicURL } from 'router/routes'

import Header from 'components/header'

import 'react-toastify/dist/ReactToastify.css'
import Loader from './loader'

const MainContainer = ({ children }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const ldClient = useLDClient()
  const [isValidUser, setIsValidUser] = useState(false)
  const { currentUser, isInitialized } = useAuthValue() || {}
  const showAppBar = useSelector(state => get(state, 'Appbar.showAppBar'), shallowEqual)
  const isLoading = useSelector(state => get(state, 'ResponseReducer.isLoading', false), shallowEqual)
  const selectedLoginDetails = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails', {}), shallowEqual)
  const allAccountsPerBusiness = useSelector(state => get(state, 'AuthReducer.userInfo.accountsPerBusiness', []), shallowEqual)
  const { businessId: existingBusinessId, accountId: existingAccountId, show } = selectedLoginDetails
  const { pathname } = location
  const isPublicPath = isPublicURL(pathname)
  const businessTitle = useMemo(() => {
    const selectedBusiness = allAccountsPerBusiness.find(row => row?.business?.id === existingBusinessId)
    return get(selectedBusiness, 'business.title')
  }, [allAccountsPerBusiness, existingBusinessId])

  useEffect(() => {
    if (!currentUser || isPublicPath) {
      setIsValidUser(false)
      return
    }

    dispatch(
      getUserInfo(response => {
        setIsValidUser(true)

        if (!response) {
          history.push(routes.app.error)
          return
        }

        const isTenantAndAccountAlreadySelected = existingBusinessId && existingAccountId
        if (isTenantAndAccountAlreadySelected && !show) {
          return
        }

        const accountsPerBusiness = get(response, 'userInfo.accountsPerBusiness', [])
        if (isSingleAccount({ accountsPerBusiness })) {
          const accountDetails = getFirstAccountDetails({ accountsPerBusiness })
          console.log(accountDetails)
          const { businessName, businessId, accountId } = accountDetails

          dispatch(setSelectedLoginDetails({ businessName, businessId, accountId, show: false, redirectToInvoicePage: false }))
          return
        }
        history.push(routes.app.selectAccount)
      })
    )
  }, [currentUser, isPublicPath])

  useEffect(() => {
    const { businessId } = selectedLoginDetails

    if (selectedLoginDetails && businessId && currentUser && isValidUser) {
      ldClient?.identify({
        kind: 'multi',
        'crm-tenant': {
          key: businessId,
        },
      })
    }
  }, [selectedLoginDetails, currentUser, isValidUser])

  if (!isInitialized) {
    return <Loader />
  }

  return (
    <>
      <Helmet>
        <title>{getPageTitle(pathname, businessTitle)}</title>
      </Helmet>

      {isLoading && <Loader />}

      {(!currentUser || isPublicPath) && children}

      {currentUser && isValidUser && !isPublicPath && (
        <>
          {showAppBar && <Header />}
          <div className="main">{children}</div>
        </>
      )}
      <ToastContainer hideProgressBar />
    </>
  )
}

MainContainer.propTypes = {
  children: PropTypes.node.isRequired,
}

export default MainContainer
